import { INJ_DENOM, NBZ_DENOM, SHU_DENOM, SOL_DENOM, USDC_NEW_DENOM, USDT_DENOM } from '@/constants/constants'
import { groupBy } from '@/helpers/groupBy'
import { create } from 'zustand'
import { createComputed } from 'zustand-computed'
import { BigNumberInWei } from '@injectivelabs/utils'
import { getTokenByCurrency, getTokenByDenom } from '@/services/token-service'
import { DoubleBet as DoubleBetQuery, DoubleColor } from '@/__generated__/graphql'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'

export const currenciesSettings = {
  minValue: {
    [INJ_DENOM]: 0.01,
    [NBZ_DENOM]: 1,
    [SHU_DENOM]: 0.1,
    [USDC_NEW_DENOM]: 0.1,
    [USDT_DENOM]: 0.1,
    [SOL_DENOM]: 0.001,
  },
  maxValue: {
    [INJ_DENOM]: 5,
    [NBZ_DENOM]: 1_000,
    [SHU_DENOM]: 100,
    [USDC_NEW_DENOM]: 75,
    [USDT_DENOM]: 75,
    [SOL_DENOM]: 1,
  },
  maxPool: {
    [INJ_DENOM]: 10,
    [NBZ_DENOM]: 2_000,
    [SHU_DENOM]: 200,
    [USDC_NEW_DENOM]: 150,
    [USDT_DENOM]: 150,
    [SOL_DENOM]: 1,
  },
}

export type GameState = {
  id: number
  denom: string
  drawTimestamp: number
  timestamp: number
  finishedTimestamp: number
  previousRoll: number | null
  currentRoll: number | null
  state: DoubleState | null
  jackpotDistribution: boolean
  jackpotPool: string
  bets: GroupedBets
  rawBets: DoubleBet[]
  isRoundFinished: boolean
  drawRound: null | number
  drawRoundTimestamp: null | number
}

export type GroupedBets = Record<DoubleColor, Array<{ amount: string; bet: string; denom: string; player: string }>>

export type DoubleBet = {
  player: string
  amount: string
  color: DoubleColor
  jackpotPayout?: string
  bet: string
  denom: string
}

export enum DoubleState {
  INIT = 'INIT',
  PROCESSING = 'PROCESSING',
  DRAWING = 'DRAWING',
  FINISHED = 'FINISHED',
}

const initialState = {
  id: '0',
  drawTimestamp: 0,
  finishedTimestamp: 0,
  timestamp: 0,
  previousRoll: null,
  currentRoll: null,
  state: null,
  jackpotDistribution: false,
  jackpotPool: '0',
  rawBets: [],
  isRoundFinished: false,
  drawRound: null,
  drawRoundTimestamp: null,
  bets: {
    [DoubleColor.Black]: [],
    [DoubleColor.Red]: [],
    [DoubleColor.Green]: [],
  },
  isJackpotDistribution: false,
  jackpotAmountUSDC: '0',
  lastColors: {
    history: [],
    statistics: {
      [DoubleColor.Black]: 0,
      [DoubleColor.Red]: 0,
      [DoubleColor.Green]: 0,
    },
  },

  denom: INJ_DENOM,
  errors: [],
  isDirty: false,
  amount: '0.1',
  minValue: currenciesSettings.minValue[INJ_DENOM],
  maxValue: currenciesSettings.maxValue[INJ_DENOM],
  maxPool: currenciesSettings.maxPool[INJ_DENOM],
}

type ComputedStore = {
  maxPool: number
}

type DoubleState2 = {
  isRoundFinished: boolean
  id: string
  drawTimestamp: number
  finishedTimestamp: number
  timestamp: number
  previousRoll: number | null
  currentRoll: number | null
  state: DoubleState | null
  bets: {
    [DoubleColor.Red]: DoubleBet[]
    [DoubleColor.Black]: DoubleBet[]
    [DoubleColor.Green]: DoubleBet[]
  }
  isJackpotDistribution: boolean
  jackpotAmountUSDC: string
  drawRound: number | null
  drawRoundTimestamp: number | null
  lastColors: {
    history: Array<DoubleColor>
    statistics: {
      [DoubleColor.Black]: number
      [DoubleColor.Red]: number
      [DoubleColor.Green]: number
    }
  }

  denom: string
  errors: Array<string>
  amount: string
  minValue: number
  maxValue: number
  isDirty: boolean

  setValues: <K extends keyof FormFields>({}: Partial<Record<K, { value: FormFields[K]; setDirty?: boolean }>>) => void
  setDenom: (denom: string) => void
  formRevalidation: () => void
}

export const tranformDoubleStatistics = (data: any) => {
  return {
    history: data.result.map((color: string) => getDoubleColor(color)),
    statistics: {
      [DoubleColor.Black]: data.statistics['black'],
      [DoubleColor.Red]: data.statistics['red'],
      [DoubleColor.Green]: data.statistics['green'],
    },
  }
}

const getDoubleColor = (c: string) => {
  switch (c) {
    case 'r':
      return DoubleColor.Red
    case 'b':
      return DoubleColor.Black
    case 'g':
      return DoubleColor.Green
  }
}

const computed = createComputed((state: DoubleState2): ComputedStore => {
  return {
    maxPool: currenciesSettings.maxPool[state.denom],
  }
})

export const useDoubleStore = create<DoubleState2>()(
  computed((set, get) => ({
    formRevalidation: () => {
      const state = get()
      const { fields, errors } = formValidation({
        amount: state.amount,
        denom: state.denom,
        minValue: state.minValue,
        maxValue: state.maxValue,
      })
      set({
        errors,
        isDirty: true,
        ...fields,
      })
    },
    setDenom: (denom: string) => {
      const minValue = currenciesSettings.minValue[denom]

      set(() => {
        const { fields, errors } = formValidation({
          amount: (minValue * 10).toString(),
          denom: denom,
          minValue,
          maxValue: currenciesSettings.maxValue[denom],
        })

        return {
          errors,
          ...fields,
          isDirty: true,
        }
      })
    },
    setValues: (values) => {
      set((state) => {
        let newFields = {
          amount: state.amount,
          denom: state.denom,
          minValue: state.minValue,
          maxValue: state.maxValue,
        }

        let isDirty = false
        for (const key in values) {
          newFields = {
            ...newFields,
            [key]: values[key]!.value,
          }

          isDirty = isDirty || values[key]!.setDirty !== false
        }

        const { fields, errors } = formValidation(formParser(newFields))

        sessionStorage.setItem('double-form', JSON.stringify(fields))

        return {
          errors,
          ...fields,
          isDirty: !isDirty ? state.isDirty : true,
        }
      })
    },
    ...initialState,
  }))
)

export const transformGameState = (data: any) => {
  return {
    id: data.game_id as string,
    drawTimestamp: data.finish_accept_bet as number,
    finishedTimestamp: data.finished_timestamp ? (data.finished_timestamp as number) : 0,
    timestamp: data.timestamp as number,
    previousRoll: data.previous_roll as number,
    currentRoll: data.win_number === '' ? null : (data.win_number as number),
    state: getDoubleState(data.state),
    bets: groupDoubleBetsByColor(transformDoubleBets(data.bets)),
    lastColors: tranformDoubleStatistics(data.lastColors),
    isJackpotDistribution: Boolean(data.win_jackpot),
    jackpotAmountUSDC: (data.jackpot_pool as string) || '0',
    drawRound: data.draw_round === 0 ? null : (data.draw_round as number),
    drawRoundTimestamp: data.draw_timestamp === 0 ? null : (data.draw_timestamp as number),
  }
}

const getDoubleState = (state: any) => {
  switch (state) {
    case 'pending':
      return DoubleState.INIT
    case 'started':
      return DoubleState.PROCESSING
    case 'rolling':
      return DoubleState.DRAWING
    case 'finished':
      return DoubleState.FINISHED
    case 'distributing':
      return DoubleState.FINISHED
    default:
      return DoubleState.INIT
  }
}

export const groupBetsByJackpotDistribution = (bets?: Array<Partial<DoubleBetQuery>>) => {
  if (!bets?.length) {
    return []
  }

  const betsGroupedByUser = Object.entries(groupBy(bets, (bet) => bet?.address || '')).map(([_, bets]) => {
    return bets.slice(1).reduce(
      (prev, curr) => ({
        player: (prev as any).player as string,
        denom: prev.denom as string,
        color: prev.color as DoubleColor,
        amount: new BigNumberInWei(curr.amount || 0).plus(prev.amount || 0).toString(),
        bet: new BigNumberInWei(curr.jackpotPayout || 0).plus(prev.jackpotPayout || 0).toString(),
        jackpotPayout: new BigNumberInWei(curr.jackpotPayout || 0).plus(prev.jackpotPayout || 0).toString(),
      }),
      {
        player: bets.at(0)!.address as string,
        denom: getTokenByCurrency(SupportedCurrencies.USDC).denom,
        color: DoubleColor.Green,
        amount: bets.at(0)?.amount as string,
        bet: bets.at(0)?.jackpotPayout as string,
        jackpotPayout: bets.at(0)?.jackpotPayout as string,
      }
    )
  })

  return betsGroupedByUser
}

export const transformDoubleBets = (bets: Array<Partial<DoubleBetQuery> | null>) => {
  if (!bets?.length) {
    return []
  }

  return bets
    .filter((bet) => bet !== null)
    .map((bet) => ({
      player: bet.address || '',
      bet: (bet.bet as string) || '0',
      amount: (bet.amount as string) || '0',
      denom: (bet.denom as string) || INJ_DENOM,
      jackpotPayout: bet.jackpotPayout as string | undefined,
      color: bet.color as DoubleColor,
    }))
}

export const groupDoubleBetsByColor = (bets: Array<DoubleBet>) => {
  const groupedBets = groupBy(bets, (bet) => bet.color.toUpperCase() as DoubleColor)

  return {
    [DoubleColor.Black]: groupDoubleBetsByUsersAndDenom(groupedBets[DoubleColor.Black] || []),
    [DoubleColor.Red]: groupDoubleBetsByUsersAndDenom(groupedBets[DoubleColor.Red] || []),
    [DoubleColor.Green]: groupDoubleBetsByUsersAndDenom(groupedBets[DoubleColor.Green] || []),
  }
}

export const groupDoubleBetsByUsersAndDenom = (bets: Array<DoubleBet>) => {
  return Object.entries(
    bets.reduce(
      (acc, { player, amount, denom, bet, jackpotPayout }) => {
        const key = `${player}-${denom}`
        if (!acc[key]) {
          acc[key] = {
            player,
            denom,
            amount: new BigNumberInWei(0),
            bet: new BigNumberInWei(0),
            jackpotPayout: new BigNumberInWei(0),
          }
        }

        acc[key].amount = acc[key].amount.plus(amount)
        acc[key].bet = acc[key].bet.plus(bet)
        acc[key].jackpotPayout = acc[key].jackpotPayout.plus(jackpotPayout || '0')
        return acc
      },
      {} as unknown as any
    )
  ).map(([_, bet]) => {
    const data = bet as DoubleBet
    return {
      player: data.player,
      denom: data.denom,
      amount: data.amount.toString(),
      bet: data.bet.toString(),
      jackpotPayout: data.jackpotPayout?.toString(),
    }
  }) as Array<DoubleBet>
}

const formParser = (values: FormFields) => {
  const { amount } = values
  const token = getTokenByDenom(values.denom)
  const fields = {
    amount,
    minValue: currenciesSettings.minValue[values.denom]!,
    maxValue: currenciesSettings.maxValue[values.denom]!,
  }

  if (isNaN(Number(amount)) || Number(amount) < 0) {
    fields.amount = ''
    return { ...values, ...fields }
  }

  if (Number(amount) > fields.maxValue) {
    fields.amount = fields.maxValue.toString()
  }

  const regex = new RegExp(`^\\d+(\\.\\d{0,${token.precisions}})?$`, 'g')

  if (amount != '' && !regex.test(amount)) {
    fields.amount = Number(amount).toFixed(token.precisions)
  }

  return { ...values, ...fields }
}

type FormFields = {
  amount: string
  denom: string
  minValue: number
  maxValue: number
}

const formValidation = (fields: FormFields) => {
  let { amount, minValue } = fields

  const errors = []

  if (amount.length === 0) {
    errors.push('Amount is required')
  }

  if (Number(amount) < minValue) {
    errors.push('Min amount is ' + minValue)
  }

  return {
    fields,
    errors,
  }
}
